import {
    Box,
    Button,
    CircularProgress,
    Grid,
    Link,
    List,
    ListItem,
    Modal,
    ModalActions,
    ModalContent,
    ModalTitle,
    Option,
    Pagination,
    SelectField,
    TextField,
    Typography,
} from '@deere/fuel-react';
import { ReactComponent as AddIcon } from '@deere/ux.brand-foundations/icons/add.svg';
import { ReactComponent as AddCircleIcon } from '@deere/ux.brand-foundations/icons/add_circle.svg';
import DownloadIcon from '@deere/ux.brand-foundations/icons/download.svg';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Breadcrumbs } from '@mui/material';
import { DateTime } from 'luxon';
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertBox } from '../../components/AlertBox';
import { CopyJobAndPasteAgain } from '../../components/CopyJobAndPasteAgain';
import { JobDetails } from '../../components/JobDetails';
import { WorkforceJobBidRow } from '../../components/WorkforceJobBidRow';
import { AddBidderModal } from '../../components/admin-modals/AddBidderModal';
import {
    ASSIGNMENT_TYPE,
    BIDDER_ALREADY_WON_OTHER_BID_ERROR,
    JOB_PHASE,
    JOB_STATUS,
    WORKFORCE_JOB_BID_STATUS,
} from '../../constants.js';
import { LoggedInContext } from '../../context/LoggedInContext';
import { useGoBack } from '../../hooks/useGoBack';
import { errorBannerFromResponse } from '../../utils/errorBanner';
import styles from './ClosedJobDetails.module.css';

export const ClosedJobDetails = () => {
    const { jobNumber } = useParams();
    const [job, setJob] = useState();
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [alertBanner, setAlertBanner] = useState(null);
    const [bidTypeDisplayed, setBidTypeDisplayed] = useState('');
    const [filteredBidList, setFilteredBidList] = useState([]);
    const [addBidModalOpen, setAddBidModalOpen] = useState(false);
    const [openings, setOpenings] = useState([]);
    const [workforceOptions, setWorkforceOptions] = useState([]);
    const [assignSuccessModalOpen, setAssignSuccessModalOpen] = useState(false);
    const [notes, setNotes] = useState('');
    const navigate = useNavigate();
    const goBack = useGoBack('/closed-jobs');
    const { makeRequest, authState } = useContext(LoggedInContext);
    const activeBidderError =
        'Due to technical issues, we are unable to retrieve bidders for this job. Please try again later.';
    const jobBidError =
        'Due to technical issues, we are unable to retrieve this job bid. Please try again later.';

    const itemsPerPage = 10;
    const noOfPages = Math.ceil(filteredBidList.length / itemsPerPage);
    const [page, setPage] = React.useState(1);
    const unsuccessfulOrWithdrawn = 'Unsuccessful or Withdrawn';
    const allBidders = 'ALL_BIDDERS';

    const handleChange = (_event, value) => {
        setPage(value);
    };

    // Sorting by admin rank starts
    const activeComparator = (a, b) => {
        const rankA = a.adminRank ?? Number.MAX_SAFE_INTEGER;
        const rankB = b.adminRank ?? Number.MAX_SAFE_INTEGER;
        return rankA - rankB;
    };
    const withdrawnComparator = (a, b) => {
        const nameA = a.name?.toUpperCase(); // ignore upper and lowercase
        const nameB = b.name?.toUpperCase(); // ignore upper and lowercase
        if (nameA < nameB) {
            return -1;
        }
        if (nameA > nameB) {
            return 1;
        }
        return 0;
    };
    const getComparator = () => {
        if (
            bidTypeDisplayed === WORKFORCE_JOB_BID_STATUS.WITHDRAWN ||
            bidTypeDisplayed === unsuccessfulOrWithdrawn
        ) {
            return withdrawnComparator;
        }
        return activeComparator;
    };
    // Sorting by admin rank ends

    const bidderAdded = (newBidder) => {
        if (newBidder) {
            if (bidTypeDisplayed === WORKFORCE_JOB_BID_STATUS.SUBMITTED) {
                setFilteredBidList([...filteredBidList, newBidder]);
            }
            // prettier-ignore
            setAlertBanner({ variant: 'success', message: 'You have added a new bidder to the list! The new bidder\'s name will appear in alphabetical order by first name on the active bidder list.' });
            const updatedBids = [
                ...job.workforceJobBidList.filter(
                    (jobBid) =>
                        jobBid.status === WORKFORCE_JOB_BID_STATUS.SUBMITTED
                ),
                newBidder,
            ];
            updatedBids.sort(
                (a, b) =>
                    (a.adminRank ?? Number.MAX_SAFE_INTEGER) -
                    (b.adminRank ?? Number.MAX_SAFE_INTEGER)
            );
            setJob({
                ...job,
                workforceJobBidList: updatedBids,
            });

            const updatedOpenings = openings.map((o, idx) =>
                o.workforceJobBid?.racf === newBidder.racf
                    ? { openingIndex: idx + 1, value: '' }
                    : o
            );
            setOpenings(updatedOpenings);
        }
    };

    const bidTypeDisplayHandler = (displayType) => {
        if (job === undefined) {
            setAlertBanner({
                variant: 'error',
                message: activeBidderError,
            });
        } else {
            setBidTypeDisplayed(displayType);
            if (
                displayType === unsuccessfulOrWithdrawn ||
                displayType === WORKFORCE_JOB_BID_STATUS.WITHDRAWN
            ) {
                const nonActiveFilteredBids = job.workforceJobBidList.filter(
                    (bid) =>
                        bid.status === WORKFORCE_JOB_BID_STATUS.REMOVED ||
                        bid.status === WORKFORCE_JOB_BID_STATUS.WITHDRAWN ||
                        bid.status === WORKFORCE_JOB_BID_STATUS.WON_OTHER_BID
                );
                setFilteredBidList(nonActiveFilteredBids);
            } else if (displayType === allBidders) {
                setFilteredBidList(job.workforceJobBidList);
            } else if (displayType === WORKFORCE_JOB_BID_STATUS.SUCCESSFUL) {
                const successfulBids = job.workforceJobBidList.filter(
                    (bid) => bid.status === WORKFORCE_JOB_BID_STATUS.ASSIGNED
                );
                setFilteredBidList(successfulBids);
            } else {
                const activeBids = job.workforceJobBidList.filter(
                    (bid) =>
                        bid.status === WORKFORCE_JOB_BID_STATUS.SUBMITTED ||
                        bid.status === WORKFORCE_JOB_BID_STATUS.ASSIGNED
                );
                setFilteredBidList(activeBids);
            }
        }
    };

    const getBidsDisplayedTitle = () => {
        if (
            job.status === JOB_STATUS.PENDING &&
            job.jobPhase === JOB_PHASE.CLOSED
        ) {
            if (bidTypeDisplayed !== unsuccessfulOrWithdrawn) {
                return `Active Bidder Seniority (${filteredBidList.length})`;
            } else {
                return `Unsuccessful or Withdrawn Bidders (${filteredBidList.length})`;
            }
        } else if (
            job.status !== JOB_STATUS.ASSIGNED &&
            bidTypeDisplayed === WORKFORCE_JOB_BID_STATUS.SUBMITTED
        ) {
            return `Active Bidders (${filteredBidList.length})`;
        } else if (job.status === JOB_STATUS.ASSIGNED) {
            if (bidTypeDisplayed === WORKFORCE_JOB_BID_STATUS.SUCCESSFUL) {
                return `Successful Bidders (${filteredBidList.length})`;
            } else if (bidTypeDisplayed === allBidders) {
                return `All Bidders (${filteredBidList.length})`;
            }
        } else {
            return `Withdrawn Bidders (${filteredBidList.length})`;
        }
    };

    const markJobAssignedHandler = async () => {
        const requestOptions = {
            method: 'POST',
        };
        const response = await makeRequest(
            `${process.env.REACT_APP_API_ENDPOINT}/jobs/${jobNumber}/assign`,
            requestOptions
        );
        if (response.ok) {
            navigate('/closed-jobs?phase=ready');
        } else {
            setAlertBanner(
                errorBannerFromResponse(
                    response,
                    'We were unable to save and set assignments. This job will remain on the Closed & Ready to Assign list. Please try again later.'
                )
            );
        }
        setAssignSuccessModalOpen(false);
    };

    const createAssignmentPayload = (assignment, opening) => {
        if (
            assignment === ASSIGNMENT_TYPE.REVOKED ||
            assignment === ASSIGNMENT_TYPE.NO_QUALIFIED_BIDDER ||
            assignment === ''
        ) {
            return {
                ...opening,
                jobId: job.id,
                assignmentType: assignment || ASSIGNMENT_TYPE.DELETE,
                workforceJobBid: null,
            };
        }
        const foundBid = job.workforceJobBidList.find(
            (bid) => bid.id === parseInt(assignment)
        );
        return {
            ...opening,
            jobId: job.id,
            workforceJobBid: foundBid,
            assignmentType: ASSIGNMENT_TYPE.ASSIGNED,
        };
    };

    const openingsAssignmentHandler = async (assignment, opening) => {
        const assignmentObject = createAssignmentPayload(assignment, opening);
        const previousOpenings = openings;

        setOpenings(
            openings.map((o) => {
                if (o.openingIndex === opening.openingIndex) {
                    return {
                        ...o,
                        ...assignmentObject,
                        value:
                            assignmentObject.assignmentType ===
                            ASSIGNMENT_TYPE.ASSIGNED
                                ? assignmentObject.workforceJobBid.id
                                : assignmentObject.assignmentType,
                    };
                } else {
                    return o;
                }
            })
        );

        const requestOptions = {
            method: 'POST',
            body: JSON.stringify(assignmentObject),
        };

        const assignResponse = await makeRequest(
            `${process.env.REACT_APP_API_ENDPOINT}/jobs/${jobNumber}/assignment`,
            requestOptions
        );

        if (!assignResponse.ok) {
            setOpenings(previousOpenings);
            if (assignResponse.status === 409) {
                const conflict = await assignResponse.json().catch(() => null);
                setAlertBanner({
                    variant: 'error',
                    message:
                        conflict?.detail ??
                        conflict?.message ??
                        BIDDER_ALREADY_WON_OTHER_BID_ERROR,
                });
            } else {
                setAlertBanner(
                    errorBannerFromResponse(
                        assignResponse,
                        'We were unable to save your changes. Please try again later.'
                    )
                );
            }
        } else {
            const assignmentDTO = await assignResponse.json();
            // doing this twice because the first time is to update the UI,
            // second time is to update the opening with the new assignment ID
            setOpenings(
                openings.map((o) => {
                    if (o.openingIndex === opening.openingIndex) {
                        let value;
                        if (
                            assignmentDTO.assignmentType ===
                            ASSIGNMENT_TYPE.ASSIGNED
                        ) {
                            value = assignmentDTO.workforceJobBid.id;
                        } else if (
                            assignmentDTO.assignmentType ===
                            ASSIGNMENT_TYPE.DELETE
                        ) {
                            return { openingIndex: o.openingIndex, value: '' };
                        } else {
                            value = assignmentDTO.assignmentType;
                        }

                        return {
                            ...o,
                            ...assignmentDTO,
                            value,
                        };
                    } else {
                        return o;
                    }
                })
            );
        }

        resetAssignmentSelections();
    };

    const isWorkforceOptionDisabled = (workforceOption) => {
        if (!workforceOption) {
            return false;
        }
        const assignmentIdsList = Object.values(openings).map(
            (a) =>
                a.assignmentType === ASSIGNMENT_TYPE.ASSIGNED &&
                (a.workforceJobBid?.id ?? null)
        );
        return assignmentIdsList.includes(workforceOption?.id);
    };

    const hasWonOtherBid = (workforceOption) => workforceOption?.jobWon != null;

    const resetAssignmentSelections = () => {
        const availableBids = job.workforceJobBidList.filter(
            (jobBid) => jobBid.status === WORKFORCE_JOB_BID_STATUS.SUBMITTED
        );
        availableBids.sort(
            (a, b) =>
                (a.adminRank ?? Number.MAX_SAFE_INTEGER) -
                (b.adminRank ?? Number.MAX_SAFE_INTEGER)
        );
        setWorkforceOptions(availableBids);
    };

    const addAnotherOpening = async () => {
        setIsSaving(true);
        const requestOptions = {
            method: 'POST',
            body: JSON.stringify({
                ...job,
                noOfOpenings: ++job.noOfOpenings,
            }),
        };

        const newOpening = await makeRequest(
            `${process.env.REACT_APP_API_ENDPOINT}/jobs`,
            requestOptions
        );
        if (newOpening.ok) {
            setJob({
                ...job,
                noOfOpenings: ++job.noOfOpenings,
            });

            setOpenings(
                [...openings, { value: '' }].map((o, index) => ({
                    ...o,
                    index,
                }))
            );
        } else {
            setAlertBanner(
                errorBannerFromResponse(
                    newOpening,
                    'We were unable to update this job. Please try again later.'
                )
            );
        }
        setIsSaving(false);
    };
    const addBidderToJob = async (bidder) => {
        const requestOptions = {
            method: 'POST',
            body: JSON.stringify(bidder),
        };
        const response = await makeRequest(
            `${process.env.REACT_APP_API_ENDPOINT}/job/${jobNumber}/bidders`,
            requestOptions
        );
        if (response.status === 200) {
            const bidderResponse = await response.json();
            setAddBidModalOpen(false);
            bidderAdded(bidderResponse, response.status);
        } else if (response.status === 409 || response.status === 422) {
            setAddBidModalOpen(false);
            setAlertBanner(
                errorBannerFromResponse(
                    response,
                    'Cannot add duplicate bidder. This bidder is already added in this job.'
                )
            );
        } else {
            setAddBidModalOpen(false);
            setAlertBanner(
                errorBannerFromResponse(
                    response,
                    'We were unable to save your changes. Please try again later.'
                )
            );
        }
    };

    const refreshActiveList = (id) => {
        if (id !== null)
            setFilteredBidList(
                filteredBidList.filter((bid1) => bid1.racf !== id)
            );
        else {
            setAlertBanner({ variant: 'error', message: activeBidderError });
        }
    };

    const refreshOpenings = (racf) => {
        if (racf !== null) {
            const updatedOpenings = openings.map((o, idx) =>
                o.workforceJobBid?.racf === racf
                    ? { openingIndex: idx + 1, value: '' }
                    : o
            );
            setOpenings(updatedOpenings);

            setJob({
                ...job,
                workforceJobBidList: job.workforceJobBidList.map((bid) =>
                    bid.racf === racf
                        ? { ...bid, status: WORKFORCE_JOB_BID_STATUS.REMOVED }
                        : bid
                ),
            });

            resetAssignmentSelections();
        }
    };

    async function excelDownload(response) {
        const filename = response.headers
            .get('content-disposition')
            .split('filename=')[1];
        const downloadResponse = await response.blob();
        const fileURL = window.URL.createObjectURL(downloadResponse);
        const link = document.createElement('a');
        link.href = fileURL;
        link.download = filename;
        link.target = '_blank';
        link.click();
        window.URL.revokeObjectURL(fileURL);
        link.remove();
    }

    const downloadClosedReadyJobs = async () => {
        const response = await makeRequest(
            process.env.REACT_APP_API_ENDPOINT +
                `/DownloadReadyToAssignJobBids/${jobNumber}`
        );

        if (response.ok) {
            await excelDownload(response);
        } else {
            setAlertBanner(
                errorBannerFromResponse(
                    response,
                    'We are unable to download the closed jobs spreadsheet.'
                )
            );
        }
    };

    const closedAssignedAllBidderDownload = async () => {
        const response = await makeRequest(
            `${process.env.REACT_APP_API_ENDPOINT}/jobs/${jobNumber}/closedAssignedAllBiddersDownload`
        );
        if (response.ok) {
            await excelDownload(response);
        } else {
            setAlertBanner(
                errorBannerFromResponse(
                    response,
                    'We are unable to download the jobs spreadsheet.'
                )
            );
        }
    };

    const successfulBidderDownload = async () => {
        const response = await makeRequest(
            `${process.env.REACT_APP_API_ENDPOINT}/jobs/${jobNumber}/successfulBiddersDownload`
        );
        if (response.ok) {
            await excelDownload(response);
        } else {
            setAlertBanner(
                errorBannerFromResponse(
                    response,
                    'We are unable to download the jobs spreadsheet.'
                )
            );
        }
    };

    const updateAndSaveNotesHandler = async () => {
        const updatedJob = { ...job, notes: notes };
        const requestOptions = {
            method: 'POST',
            body: JSON.stringify(updatedJob),
        };
        const response = await makeRequest(
            `${process.env.REACT_APP_API_ENDPOINT}/jobs`,
            requestOptions
        );
        if (!response.ok) {
            setAlertBanner(
                errorBannerFromResponse(
                    response,
                    'We are unable to update the notes at the moment.'
                )
            );
        }
    };

    useEffect(() => {
        if (!isLoading && job) resetAssignmentSelections();
        // eslint-disable-next-line
    }, [openings]);

    useEffect(() => {
        let ignore = false;
        const fetchActiveBid = async () => {
            setIsLoading(true);
            try {
                if (!ignore) {
                    const jobResponse = await makeRequest(
                        `${process.env.REACT_APP_API_ENDPOINT}/jobs/${jobNumber}`
                    );
                    if (jobResponse.ok) {
                        const jobDetails = await jobResponse.json();
                        if (
                            DateTime.fromISO(jobDetails.withdrawalStartDate) <=
                                DateTime.now() &&
                            DateTime.fromISO(jobDetails.withdrawalEndDate) >=
                                DateTime.now()
                        ) {
                            setAlertBanner({
                                variant: 'info',
                                message:
                                    'This job is currently in the Bid Withdrawal Period.',
                            });
                        }
                        jobDetails.status = jobDetails.status.trim();
                        setJob({
                            ...jobDetails,
                        });
                        setNotes(jobDetails.notes);
                        const availableBids =
                            jobDetails.workforceJobBidList.filter(
                                (jobBid) =>
                                    jobBid.status ===
                                    WORKFORCE_JOB_BID_STATUS.SUBMITTED
                            );
                        availableBids.sort(
                            (a, b) =>
                                (a.adminRank ?? Number.MAX_SAFE_INTEGER) -
                                (b.adminRank ?? Number.MAX_SAFE_INTEGER)
                        );
                        const allOpenings = new Array(jobDetails.noOfOpenings)
                            .fill({})
                            .map((_, i) => {
                                const index = i + 1;
                                const pendingBid =
                                    jobDetails.assignmentList.find(
                                        (a) => a.openingIndex === index
                                    );
                                if (pendingBid) {
                                    return {
                                        ...pendingBid,
                                        value:
                                            pendingBid.assignmentType ===
                                            ASSIGNMENT_TYPE.ASSIGNED
                                                ? pendingBid.workforceJobBid.id
                                                : pendingBid.assignmentType,
                                    };
                                }
                                return {
                                    openingIndex: index,
                                    value: '',
                                };
                            });
                        setOpenings(allOpenings);
                        setWorkforceOptions(availableBids);
                    } else {
                        setAlertBanner(
                            errorBannerFromResponse(jobResponse, jobBidError)
                        );
                    }
                }
            } catch (err) {
                setAlertBanner(errorBannerFromResponse(err, jobBidError));
            } finally {
                setIsLoading(false);
            }
        };
        fetchActiveBid();
        // cleanup function that will ignore future results of fetch triggered by the useEffect hook
        return () => {
            ignore = true;
        };
    }, [jobNumber, authState, makeRequest]);

    useEffect(() => {
        if (!isLoading && job) {
            if (
                job.status === JOB_STATUS.PENDING &&
                job.jobPhase === JOB_PHASE.CLOSED
            ) {
                bidTypeDisplayHandler(WORKFORCE_JOB_BID_STATUS.SUBMITTED);
            }
        }
        // eslint-disable-next-line
    }, [isLoading]);

    const getNumberOfAssignedJobs = () =>
        openings.filter((a) => !!a.assignmentType).length;

    return (
        <div className={styles.pageLevelParent}>
            <Box spacing={6} className={styles.pageLevelContent}>
                <Breadcrumbs>
                    <Link href="/closed-jobs" onClick={goBack}>
                        Closed Job Bids
                    </Link>
                    <Typography color="textPrimary">Job {jobNumber}</Typography>
                </Breadcrumbs>
                <Box className={styles.alertSection}>
                    {alertBanner && (
                        <AlertBox
                            msg={alertBanner.message}
                            variant={alertBanner.variant}
                        />
                    )}
                </Box>
                {isLoading && (
                    <CircularProgress
                        size="lg"
                        variant="indeterminate"
                        title={'Loading'}
                    />
                )}
                {!isLoading && job && (
                    <>
                        <Box className={styles.boxForButton}>
                            <Typography
                                variant="h1"
                                className={styles.pageLevelHeading}
                            >
                                #{jobNumber}
                            </Typography>
                            <CopyJobAndPasteAgain bidobj={job} />
                        </Box>
                        <JobDetails bidObj={job} />

                        {job.status === JOB_STATUS.CANCELED && (
                            <Box>
                                <Grid
                                    container
                                    spacing={4}
                                    className={styles.canceledNotesGridSection}
                                >
                                    <Grid item sm={12}>
                                        <span>
                                            This job was marked as posted in
                                            error. There are no job bid winners
                                            for this job.
                                        </span>
                                    </Grid>
                                    <Grid item sm={12}>
                                        <span className={styles.boldData}>
                                            Notes:{' '}
                                        </span>
                                        <span>{job.notes}</span>
                                    </Grid>
                                </Grid>
                            </Box>
                        )}

                        {job.status === JOB_STATUS.ASSIGNED && (
                            <Grid
                                container
                                spacing={4}
                                className={styles.biddersGridSection}
                            >
                                <Grid item sm={6}>
                                    <Typography
                                        variant="h4"
                                        className={styles.pageLevelHeading}
                                    >
                                        Assignment Summary
                                    </Typography>
                                </Grid>
                                <Grid item sm={6}>
                                    <Typography
                                        className={styles.successfulBidStatus}
                                        variant="body1"
                                    >
                                        <WarningAmberIcon
                                            className={styles.alertIcon}
                                        />
                                        {`${
                                            openings.filter(
                                                (a) =>
                                                    a.assignmentType ===
                                                    ASSIGNMENT_TYPE.ASSIGNED
                                            ).length
                                        } of ${
                                            job.noOfOpenings
                                        } Bidder(s) Selected`}
                                    </Typography>
                                </Grid>
                                <Grid item sm={12}>
                                    <List>
                                        {openings.map((opening, index) => (
                                            <ListItem
                                                key={
                                                    opening.openingIndex ??
                                                    index
                                                }
                                                className={
                                                    styles.openingSummaryRow
                                                }
                                            >
                                                <Grid
                                                    container
                                                    alignItems="center"
                                                >
                                                    <Grid item sm={3}>
                                                        <Typography
                                                            variant="body1"
                                                            className={
                                                                styles.boldData
                                                            }
                                                        >
                                                            Opening{' '}
                                                            {opening.openingIndex ||
                                                                index + 1}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item sm={9}>
                                                        <Typography variant="body1">
                                                            {opening.assignmentType ===
                                                            ASSIGNMENT_TYPE.ASSIGNED
                                                                ? `Assigned - ${
                                                                      opening
                                                                          .workforceJobBid
                                                                          ?.name ||
                                                                      opening
                                                                          .workforceJobBid
                                                                          ?.racf ||
                                                                      'N/A'
                                                                  }`
                                                                : opening.assignmentType ===
                                                                  ASSIGNMENT_TYPE.NO_QUALIFIED_BIDDER
                                                                ? 'No Qualified Bidder'
                                                                : opening.assignmentType ===
                                                                  ASSIGNMENT_TYPE.REVOKED
                                                                ? 'Job Opening Revoked'
                                                                : 'Unassigned'}
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            </ListItem>
                                        ))}
                                    </List>
                                </Grid>
                            </Grid>
                        )}

                        {job.status === JOB_STATUS.PENDING &&
                            job.jobPhase === JOB_PHASE.CLOSED && (
                                <Grid
                                    container
                                    spacing={4}
                                    className={styles.biddersGridSection}
                                >
                                    <Grid item sm={6}>
                                        <Typography
                                            variant="h4"
                                            className={styles.pageLevelHeading}
                                        >
                                            Successful Job Bidders
                                        </Typography>
                                    </Grid>
                                    <Grid item sm={6}>
                                        <Typography
                                            className={
                                                styles.successfulBidStatus
                                            }
                                            variant="body1"
                                        >
                                            <WarningAmberIcon
                                                className={styles.alertIcon}
                                            />
                                            {`${getNumberOfAssignedJobs()} of ${
                                                job.noOfOpenings
                                            } Bidder(s) Selected`}
                                        </Typography>
                                    </Grid>
                                    <Grid item sm={12}>
                                        Your work will auto-save. Once all
                                        assignments have been made, click on Set
                                        Assignments.
                                    </Grid>
                                    {openings.map((opening, index) => {
                                        return (
                                            <Grid item sm={3} key={index}>
                                                <SelectField
                                                    value={opening.value}
                                                    required={true}
                                                    label={`Opening ${
                                                        index + 1
                                                    }`}
                                                    onChange={(event) =>
                                                        openingsAssignmentHandler(
                                                            event.target.value,
                                                            opening
                                                        )
                                                    }
                                                >
                                                    <Option value="">
                                                        Choose Winner
                                                    </Option>
                                                    <Option
                                                        value={
                                                            ASSIGNMENT_TYPE.REVOKED
                                                        }
                                                    >
                                                        Job Opening Revoked
                                                    </Option>
                                                    <Option
                                                        value={
                                                            ASSIGNMENT_TYPE.NO_QUALIFIED_BIDDER
                                                        }
                                                    >
                                                        No Qualified Bidder
                                                    </Option>
                                                    {workforceOptions.map(
                                                        (workforce, i) => {
                                                            const wonOtherBid =
                                                                hasWonOtherBid(
                                                                    workforce
                                                                );
                                                            return (
                                                                <Option
                                                                    value={
                                                                        workforce.id
                                                                    }
                                                                    key={i}
                                                                    hidden={isWorkforceOptionDisabled(
                                                                        workforce
                                                                    )}
                                                                    disabled={
                                                                        isWorkforceOptionDisabled(
                                                                            workforce
                                                                        ) ||
                                                                        wonOtherBid
                                                                    }
                                                                >
                                                                    {
                                                                        workforce.name
                                                                    }{' '}
                                                                    -{' '}
                                                                    {
                                                                        workforce.racf
                                                                    }
                                                                    {wonOtherBid
                                                                        ? ' - Won other bid'
                                                                        : ''}
                                                                </Option>
                                                            );
                                                        }
                                                    )}
                                                </SelectField>
                                            </Grid>
                                        );
                                    })}
                                    <Grid
                                        container
                                        item
                                        xs={12}
                                        justifyContent={'space-between'}
                                    >
                                        <Box className={styles.boxForButton}>
                                            <Button
                                                variant="secondary"
                                                startIcon={
                                                    isSaving ? (
                                                        <CircularProgress
                                                            variant="indeterminate"
                                                            title={'Saving'}
                                                        />
                                                    ) : (
                                                        <AddCircleIcon />
                                                    )
                                                }
                                                onClick={() =>
                                                    addAnotherOpening()
                                                }
                                            >
                                                Add Another Opening
                                            </Button>
                                        </Box>
                                        {getNumberOfAssignedJobs() ===
                                            job.noOfOpenings && (
                                            <Box
                                                className={styles.boxForButton}
                                            >
                                                <Button
                                                    variant="primary"
                                                    onClick={() =>
                                                        setAssignSuccessModalOpen(
                                                            true
                                                        )
                                                    }
                                                >
                                                    Set Assignments
                                                </Button>
                                            </Box>
                                        )}
                                    </Grid>
                                </Grid>
                            )}

                        <Box className={styles.boxForButton}>
                            <Grid container>
                                <Grid item sm={3}>
                                    <SelectField
                                        value={bidTypeDisplayed}
                                        onChange={(event) =>
                                            bidTypeDisplayHandler(
                                                event.target.value
                                            )
                                        }
                                        required={true}
                                        label="Choose Bidder Type to View"
                                    >
                                        <Option disabled hidden value="">
                                            Choose Bidder Type
                                        </Option>
                                        {job.status !== JOB_STATUS.ASSIGNED && (
                                            <Option
                                                value={
                                                    WORKFORCE_JOB_BID_STATUS.SUBMITTED
                                                }
                                            >
                                                Active Bidders
                                            </Option>
                                        )}
                                        {job.status === JOB_STATUS.PENDING &&
                                            job.jobPhase ===
                                                JOB_PHASE.CLOSED && (
                                                <Option
                                                    value={
                                                        unsuccessfulOrWithdrawn
                                                    }
                                                >
                                                    Unsuccessful or Withdrawn
                                                </Option>
                                            )}
                                        {job.status !== JOB_STATUS.PENDING ||
                                            (job.jobPhase !==
                                                JOB_PHASE.CLOSED && (
                                                <Option
                                                    value={
                                                        WORKFORCE_JOB_BID_STATUS.WITHDRAWN
                                                    }
                                                >
                                                    Withdrawn Bids
                                                </Option>
                                            ))}
                                        {job.status === JOB_STATUS.ASSIGNED && [
                                            <Option key="1" value={allBidders}>
                                                All Bidders for this Job
                                            </Option>,
                                            <Option
                                                key="2"
                                                value={
                                                    WORKFORCE_JOB_BID_STATUS.SUCCESSFUL
                                                }
                                            >
                                                Successful Bidders for this Job
                                            </Option>,
                                        ]}
                                    </SelectField>
                                </Grid>
                                <Grid item sm={0.5}></Grid>
                                {job.status !== JOB_STATUS.ASSIGNED && (
                                    <Grid item sm={8.5}>
                                        <Typography>
                                            Admins must check all seniority
                                            dates as some employees may have a
                                            seniority date of{' '}
                                            <span
                                                className={styles.nullSeniority}
                                            >
                                                Null
                                            </span>{' '}
                                            which indicates an issue with their
                                            employee data.
                                        </Typography>
                                    </Grid>
                                )}
                                {bidTypeDisplayed === allBidders && (
                                    <Grid
                                        item
                                        sm={8.5}
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'flex-end',
                                            alignItems: 'flex-end',
                                        }}
                                    >
                                        <Button
                                            className={styles.buttonDownload}
                                            variant="secondary"
                                            disabled={isLoading}
                                            startIcon={
                                                <img
                                                    alt=">>"
                                                    src={DownloadIcon}
                                                />
                                            }
                                            onClick={
                                                closedAssignedAllBidderDownload
                                            }
                                        >
                                            Download All Bidder List
                                        </Button>
                                    </Grid>
                                )}
                                {bidTypeDisplayed ===
                                    WORKFORCE_JOB_BID_STATUS.SUCCESSFUL && (
                                    <Grid
                                        item
                                        sm={8.5}
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'flex-end',
                                            alignItems: 'flex-end',
                                        }}
                                    >
                                        <Button
                                            className={styles.buttonDownload}
                                            variant="secondary"
                                            disabled={isLoading}
                                            startIcon={
                                                <img
                                                    alt=">>"
                                                    src={DownloadIcon}
                                                />
                                            }
                                            onClick={successfulBidderDownload}
                                        >
                                            Download Successful Bidder List
                                        </Button>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    </>
                )}
                {bidTypeDisplayed !== '' && (
                    <>
                        <Grid
                            container
                            spacing={4}
                            className={styles.biddersGridSection}
                        >
                            <Grid item sm={6}>
                                <Typography
                                    variant="h4"
                                    className={styles.pageLevelHeading}
                                >
                                    {getBidsDisplayedTitle()}
                                </Typography>
                            </Grid>
                            {job.status === JOB_STATUS.PENDING &&
                                job.jobPhase === JOB_PHASE.CLOSED &&
                                bidTypeDisplayed ===
                                    WORKFORCE_JOB_BID_STATUS.SUBMITTED && (
                                    <Grid
                                        item
                                        sm={6}
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'flex-end',
                                        }}
                                    >
                                        <Button
                                            className={styles.buttonDownload}
                                            variant="text-inline"
                                            startIcon={
                                                <img
                                                    alt=">>"
                                                    src={DownloadIcon}
                                                    width="20px"
                                                    height="20px"
                                                    style={{ color: '#367c2b' }}
                                                />
                                            }
                                            onClick={downloadClosedReadyJobs}
                                        >
                                            Download Active Bidders for This Job
                                            List
                                        </Button>
                                    </Grid>
                                )}
                            <List style={{ width: '100%' }}>
                                {filteredBidList
                                    .sort(getComparator())
                                    .slice(
                                        (page - 1) * itemsPerPage,
                                        page * itemsPerPage
                                    )
                                    .map((jobBid, index) => {
                                        return (
                                            <ListItem
                                                key={index}
                                                className={
                                                    styles.workforceJobBidRow
                                                }
                                            >
                                                <WorkforceJobBidRow
                                                    jobNumber={jobNumber}
                                                    workforceJobBid={jobBid}
                                                    refreshActiveList={
                                                        refreshActiveList
                                                    }
                                                    jobStatus={job.status}
                                                    jobPhase={job.jobPhase}
                                                    refreshOpenings={
                                                        refreshOpenings
                                                    }
                                                    updateJob={setJob}
                                                    biddingGroupId={
                                                        job.biddingGroup?.id
                                                    }
                                                />
                                            </ListItem>
                                        );
                                    })}
                            </List>
                        </Grid>
                    </>
                )}
                {!isLoading && (
                    <Box className={styles.boxForButton}>
                        <AddBidderModal
                            jobNumber={jobNumber}
                            addBidderToJob={addBidderToJob}
                            closeModal={() => setAddBidModalOpen(false)}
                            modalIsOpen={addBidModalOpen}
                        />
                        {job && job.status !== JOB_STATUS.CANCELED && (
                            <Button
                                variant="secondary"
                                onClick={() => setAddBidModalOpen(true)}
                                startIcon={<AddIcon />}
                                disabled={
                                    bidTypeDisplayed !==
                                    WORKFORCE_JOB_BID_STATUS.SUBMITTED
                                }
                            >
                                Add a Bidder to This Job
                            </Button>
                        )}
                        <Pagination
                            count={noOfPages}
                            page={page}
                            onChange={handleChange}
                            breakpoint={900}
                            style={{ justifyContent: 'flex-end' }}
                        />
                    </Box>
                )}
                <Modal
                    open={assignSuccessModalOpen}
                    onClose={() => setAssignSuccessModalOpen(false)}
                    maxWidth="xs"
                    fullWidth={true}
                >
                    <ModalTitle>Set Successful Job Bidders</ModalTitle>
                    <ModalContent>
                        This job will be moved to the Closed & Assigned list of
                        jobs. You will still be able to edit the list of
                        successful bidders if that becomes necessary.
                    </ModalContent>
                    <ModalActions>
                        <Button
                            onClick={() => setAssignSuccessModalOpen(false)}
                            variant="secondary"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={markJobAssignedHandler}
                            variant="primary"
                        >
                            Close
                        </Button>
                    </ModalActions>
                </Modal>
                {!isLoading && job?.status === JOB_STATUS.ASSIGNED && (
                    <Grid item sm={4}>
                        <TextField
                            multiline
                            minRows={3}
                            label={'Notes for Bidder Changes'}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                        />
                        <Box
                            className={styles.boxForButton}
                            style={{ justifyContent: 'flex-end' }}
                        >
                            <Button
                                onClick={updateAndSaveNotesHandler}
                                variant="primary"
                            >
                                Update & Save Notes
                            </Button>
                        </Box>
                    </Grid>
                )}
            </Box>
        </div>
    );
};
